/**
 * Carrier registry — "which carriers does the platform know about".
 *
 * MVP carries one entry: Shippo. Used only as a presence-check by
 * `isCarrierAvailable()` ("is this carrier configured at all?"). The
 * eligibility composition for shippo is driven by three separate maps:
 *
 *   - capability per origin → `lib/shipping/origin-support.ts::SHIPPO_ORIGIN_SUPPORT`
 *   - access per country    → `lib/shipping/country-access.ts::COUNTRY_ACCESS`
 *   - currency native       → `lib/shipping/native-currency.ts::NATIVE_CURRENCY`
 *
 * **History (2026-06-25 cleanup):** earlier shape carried `acceptedOrigins`
 * + `billingCurrency` per carrier. Both turned out to be flattened
 * denormalisations of the per-origin truth (`SHIPPO_ORIGIN_SUPPORT[origin].currency`)
 * — and the `billingCurrency: "USD"` literal was actively wrong (Shippo
 * bills in the origin's local currency: US→USD, GB→GBP, DE/FR→EUR).
 * Both fields removed; carrier billing currency now lives where it
 * always actually was — on `OriginConfig.currency` and
 * `seller_orders.label_cost_currency`. See handoff for full audit.
 *
 * Adding a carrier (UPS/FedEx) = register here + author its own capability
 * map alongside `SHIPPO_ORIGIN_SUPPORT`. The empty config shape is
 * intentional MVP scope — extend with per-carrier fields when a second
 * carrier actually lands (premature generalization otherwise).
 *
 * @see packages/contract/src/money.ts — CarrierId enum
 * @see lib/shipping/carrier-eligibility.ts — consumer
 * @see lib/shipping/origin-support.ts — Shippo capability + per-origin currency
 * @see .tondocs/shippo/CARRIER_ELIGIBILITY_HANDOFF_2026_06_25.md D3
 */

import type { CarrierId } from "@kraahl/contract";

// Reserved for future per-carrier configuration. Empty in MVP — see header.
export type CarrierConfig = Record<string, never>;

export const CARRIER_REGISTRY: Readonly<Record<CarrierId, CarrierConfig>> = {
  shippo: {},
};
